import { Form } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import moment from 'moment';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getItemApi, getListApi, postApi } from '../../../apiUtils';
import { Loader } from '../../../components/loader/Loader';
import { useTranslation } from '../../../contexts/translation';
import { Asset } from '../../../types/asset.types';
import { ParameterMeasurable, ParameterMeasuredCreateMobile } from '../../../types/parameter.types';
import { API_URL } from '../../../utils/apiUrl';
import {
  dateFieldOptionsWithSerialization,
  dateTimeFieldOptionsWithSerialization,
  MomentDateTimeSerializationFormat,
} from '../../../utils/dates.types';
import MobileLayout from '../../Tasks/Mobile/components/MobileLayout';
import * as Bar from '../../WorkSheets/Mobile/components/Bar';

export interface PageParams {
  assetId: string;
  assetParameterId: string;
}

export const ParametersMeasuringForm = () => {
  const transFormName = 'ParameterMeasuredMobile.Form';
  const { translate } = useTranslation();
  const history = useHistory();
  const { assetId, assetParameterId } = useParams<PageParams>();
  const backRoute = `/mobile/parameters-measuring/${assetId}`;

  const [asset, setAsset] = useState<Asset | null>(null);
  const [parameterMeasured, setParameterMeasured] = useState<ParameterMeasurable | null>(null);
  const [formData, setFormData] = useState<ParameterMeasuredCreateMobile | null>(null);
  const [isLoading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    getAsset();
    getParameterMeasured();
  }, []);

  const getAsset = async () => {
    setLoading(true);
    await getItemApi<Asset>({
      url: `${API_URL.ASSET_ITEM}`,
      params: {
        id: assetId,
      },
      callAfterSuccess: (result) => {
        setAsset(result);
      },
    });
    setLoading(false);
  };

  const getParameterMeasured = async () => {
    setLoading(true);
    await getListApi<Asset>({
      url: `${API_URL.ASSET_PARAMETER_MEASURABLE_LIST}`,
      params: {
        parentId: assetId,
      },
      callAfterSuccess: (result) => {
        const param: ParameterMeasurable[] = result.filter(
          (x) => x.id === Number(assetParameterId),
        );
        if (param.length == 0) return;

        setParameterMeasured(param[0]);
        setFormData({
          // assetParameterId: param[0].assetParameterId,
          assetParameterId: Number(assetParameterId),
          valueInt: null,
          valueBool: false,
          valueFloat: null,
          valueDate: null,
          valueDateTime: null,
          valueText: '',
        });
      },
    });
    setLoading(false);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!parameterMeasured || !formData) return <></>;

    let submitData: any = {
      assetParameterId: formData.assetParameterId,
      visible: true,
      measureToDate: moment().format(MomentDateTimeSerializationFormat),
    };

    switch (parameterMeasured!.unitBaseValueDataType) {
      case `Text`:
        submitData = { ...submitData, valueText: formData.valueText };
        break;
      case `DateTime`:
        submitData = {
          ...submitData,
          valueDateTime: formData.valueDateTime,
        };
        break;
      case `Date`:
        submitData = { ...submitData, valueDate: formData.valueDate };
        break;
      case `Float`:
        submitData = {
          ...submitData,
          valueFloat: formData.valueFloat,
        };
        break;
      case `Bool`:
        submitData = { ...submitData, valueBool: formData.valueBool };
        break;
      case `Int`:
        submitData = { ...submitData, valueInt: formData.valueInt };
        break;
    }

    setLoading(true);
    postApi({
      url: API_URL.ASSET_PARAMETER_MEASURED_POST,
      data: submitData,
      callAfterSuccess: () => {
        history.push(backRoute);
        setLoading(false);
      },
      callAfterError: () => setLoading(false),
    });
  };

  // Focus na inout
  const focusInput = (e: any) => {
    if (!parameterMeasured) return <></>;

    switch (parameterMeasured!.unitBaseValueDataType) {
      case `Text`:
        e.component.getEditor('valueText')?.focus();
        break;
      case `DateTime`:
        e.component.getEditor('valueDateTime')?.focus();
        break;
      case `Date`:
        e.component.getEditor('valueDate')?.focus();
        break;
      case `Float`:
        e.component.getEditor('valueFloat')?.focus();
        break;
      case `Int`:
        e.component.getEditor('valueInt')?.focus();
        break;
    }
  };

  const getInput = () => {
    if (!parameterMeasured) return <></>;

    switch (parameterMeasured!.unitBaseValueDataType) {
      case `Text`:
        return (
          <SimpleItem
            dataField={'valueText'}
            cssClass={'input-with-custom-label'}
            editorType={`dxTextBox`}
            editorOptions={{
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
            <RequiredRule message={translate!('Value is required', transFormName)} />
          </SimpleItem>
        );
      case `DateTime`:
        return (
          <SimpleItem
            dataField={'valueDateTime'}
            cssClass={'input-with-custom-label'}
            editorType={`dxDateBox`}
            editorOptions={{
              ...dateTimeFieldOptionsWithSerialization,
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
            <RequiredRule message={translate!('Value is required', transFormName)} />
          </SimpleItem>
        );
      case `Date`:
        return (
          <SimpleItem
            dataField={'valueDate'}
            cssClass={'input-with-custom-label'}
            editorType={`dxDateBox`}
            editorOptions={{
              ...dateFieldOptionsWithSerialization,
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
            <RequiredRule message={translate!('Value is required', transFormName)} />
          </SimpleItem>
        );
      case `Float`:
        return (
          <SimpleItem
            dataField={'valueFloat'}
            cssClass={'input-with-custom-label'}
            editorType={`dxNumberBox`}
            editorOptions={{
              showClearButton: true,
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
            <RequiredRule message={translate!('Value is required', transFormName)} />
          </SimpleItem>
        );
      case `Bool`:
        return (
          <SimpleItem
            dataField={'valueBool'}
            cssClass={'input-with-custom-label'}
            editorType={`dxCheckBox`}
            editorOptions={{
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
          </SimpleItem>
        );
      case `Int`:
        return (
          <SimpleItem
            dataField={'valueInt'}
            cssClass={'input-with-custom-label'}
            editorType={`dxNumberBox`}
            ref={inputRef}
            editorOptions={{
              showClearButton: true,
              format: `fixedPoint`,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
            <RequiredRule message={translate!('Value is required', transFormName)} />
          </SimpleItem>
        );
    }
  };

  if (isLoading || !asset || !parameterMeasured) return <Loader />;

  return (
    <MobileLayout>
      <Bar.SummaryNavBar
        title={`${asset.name!}`}
        subTitle={`${parameterMeasured.parameterName} (${parameterMeasured.unitName})`}
        onBack={() =>
          history.push({
            pathname: backRoute,
          })
        }
      />
      <div style={{ padding: 15, marginTop: '20px' }}>
        <form onSubmit={handleSubmit}>
          <Form
            colCount={1}
            formData={formData}
            showValidationSummary={true}
            labelLocation={'top'}
            onContentReady={focusInput}
          >
            <GroupItem>{getInput()}</GroupItem>

            <GroupItem cssClass={`buttons-container margin-top`}>
              <ButtonItem cssClass={'button'}>
                <ButtonOptions
                  width={'100%'}
                  height={40}
                  type={'success'}
                  icon="save"
                  text={translate!('Save', transFormName)}
                  useSubmitBehavior
                />
              </ButtonItem>
              <ButtonItem cssClass={'button'}>
                <ButtonOptions
                  text={translate!('Cancel', transFormName)}
                  type={'normal'}
                  width={'100%'}
                  height={40}
                  useSubmitBehavior={false}
                  onClick={() =>
                    history.push({
                      pathname: backRoute,
                    })
                  }
                />
              </ButtonItem>
            </GroupItem>
          </Form>
        </form>
      </div>
    </MobileLayout>
  );
};
